import { BasicWriter } from "@/components/Writer/BasicWriter";
import { DependenciesList } from "@/components/content-dependency/DependenciesList";
import { Button } from "@/components/ui/button";
import { Column } from "@/components/ui/flex";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { groupKitSkillControllerGenerateMetadataDescription } from "@/lib/gen-brain";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinnerThird,
  faWandMagicSparkles,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useDebounceCallback } from "usehooks-ts";

const METADATA_DESCRIPTION_MAX_LENGTH = 500;

type SkillContentFieldsProps = {
  skillUuid: string;
  groupUuid: string;
  kitUuid: string;
  skillName: string;
  metadataDescription: string;
  content: string;
  dependencies?: {
    kind: string;
    uuid: string;
    name: string | null;
    available?: boolean;
  }[];
  disabled?: boolean;
  onMetadataDescriptionUpdate: (value: string) => void;
  onContentUpdate: (html: string) => void;
};

export function SkillContentFields({
  skillUuid,
  groupUuid,
  kitUuid,
  skillName,
  metadataDescription: metadataDescriptionProp,
  content,
  dependencies = [],
  disabled = false,
  onMetadataDescriptionUpdate,
  onContentUpdate,
}: SkillContentFieldsProps) {
  const [metadataDescription, setMetadataDescription] = useState(
    metadataDescriptionProp,
  );
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);

  // Only reset local state when switching skills. Syncing on every prop change
  // races with debounced saves and query refetches, wiping in-progress input.
  useEffect(() => {
    setMetadataDescription(metadataDescriptionProp);
  }, [skillUuid]);

  const debouncedMetadataUpdate = useDebounceCallback(
    (value: string) => {
      onMetadataDescriptionUpdate(value);
    },
    1000,
    { maxWait: 5000 },
  );

  const handleMetadataDescriptionChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const value = event.target.value;
    setMetadataDescription(value);
    debouncedMetadataUpdate(value);
  };

  const handleGenerateDescription = async () => {
    if (!skillName.trim()) {
      toast.error("Add a skill name before generating a description.");
      return;
    }

    setIsGeneratingDescription(true);

    try {
      const response = await groupKitSkillControllerGenerateMetadataDescription(
        {
          path: { groupUuid, kitUuid, skillUuid },
          body: { name: skillName, content },
        },
      );

      const generated = response.data?.metadataDescription.trim();
      if (!generated) {
        throw new Error("No description returned");
      }

      debouncedMetadataUpdate.cancel();
      setMetadataDescription(generated);
      onMetadataDescriptionUpdate(generated);
      toast.success("Metadata description generated.");
    } catch (error) {
      console.error("Error generating metadata description:", error);
      toast.error("Failed to generate metadata description.");
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const isGenerateDisabled =
    disabled || isGeneratingDescription || !skillName.trim();

  return (
    <Column className="gap-8">
      <Column className="gap-4">
        <div className="flex items-center justify-between gap-4">
          <Label htmlFor={`skill-metadata-description-${skillUuid}`}>
            Metadata Description
          </Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleGenerateDescription}
            disabled={isGenerateDisabled}
          >
            <FontAwesomeIcon
              icon={
                isGeneratingDescription ? faSpinnerThird : faWandMagicSparkles
              }
              className={isGeneratingDescription ? "animate-spin" : undefined}
            />
            Generate Description
          </Button>
        </div>
        <Textarea
          id={`skill-metadata-description-${skillUuid}`}
          value={metadataDescription}
          onChange={handleMetadataDescriptionChange}
          placeholder="Describe what this skill does and when to use it (for AI selection). Max 500 characters."
          maxLength={METADATA_DESCRIPTION_MAX_LENGTH}
          disabled={disabled}
          rows={3}
        />
        <p className="text-muted-foreground text-xs">
          {metadataDescription.length}/{METADATA_DESCRIPTION_MAX_LENGTH}
        </p>
      </Column>

      <DependenciesList dependencies={dependencies} />

      <Column className="gap-4">
        <Label>Skill Content</Label>
        <BasicWriter
          key={`content-${skillUuid}`}
          content={content}
          onUpdate={onContentUpdate}
          placeholder="Enter the skill instructions, capabilities, and knowledge..."
          hasToolbar={false}
          disabled={disabled}
          includeSkillMentions
          excludeSkillUuid={skillUuid}
        />
      </Column>
    </Column>
  );
}
